import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDialogs } from "../hooks/use-dialogs";
import useUsersModel from "../model/users";
import { ManagedUser, Tema } from "../types/general";
import { getErrorMessage } from "../utils/errors";

export const ViewModelUserTemas = () => {
  const navigate = useNavigate();
  const userId = Number(useParams().userId);
  const model = useUsersModel();
  const { dialog, close, showConfirmation, showSuccess, showError } = useDialogs();

  const [managedUser, setManagedUser] = useState<ManagedUser | null>(null);
  const [allTemas, setAllTemas] = useState<Tema[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fail = (error: any, fallback: string) =>
    showError({ title: "Erro", message: getErrorMessage(error, fallback) });

  const load = useCallback(async () => {
    try {
      const [found, temas] = await Promise.all([model.fetchUser(userId), model.fetchAllTemas()]);
      setManagedUser(found);
      setAllTemas(temas);
    } catch (error) {
      showError({
        title: "Erro",
        message: getErrorMessage(error, "Não foi possível carregar o utilizador."),
        onConfirm: () => navigate("/utilizadores"),
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);

  const associatedIds = new Set(managedUser?.temas.map((t) => t.id));
  // Temas completos (com contagens) dos associados, e os que ainda podem ser adicionados
  const associated = allTemas.filter((t) => associatedIds.has(t.id!));
  const available = allTemas.filter((t) => !associatedIds.has(t.id!));

  const addTemas = async (temas: Tema[]) => {
    if (!managedUser || temas.length === 0) return false;
    setSaving(true);
    try {
      for (const tema of temas) {
        await model.associateTema(managedUser.id, tema.id!);
      }
      showSuccess({
        title: "Sucesso",
        message: temas.length === 1 ? "Tema associado com sucesso!" : `${temas.length} temas associados com sucesso!`,
      });
      return true;
    } catch (error) {
      fail(error, "Não foi possível associar o tema.");
      return false;
    } finally {
      // Recarrega mesmo em caso de erro: alguns temas podem já ter sido associados
      setManagedUser(await model.fetchUser(managedUser.id).catch(() => managedUser));
      setSaving(false);
    }
  };

  const removeTema = (tema: Tema) => {
    if (!managedUser) return;
    showConfirmation({
      title: "Remover associação",
      message: `Remover o tema "${tema.nome}" de ${managedUser.name}?`,
      onConfirm: async () => {
        close();
        try {
          await model.disassociateTema(managedUser.id, tema.id!);
          setManagedUser({ ...managedUser, temas: managedUser.temas.filter((t) => t.id !== tema.id) });
          showSuccess({ title: "Sucesso", message: "Tema removido com sucesso!" });
        } catch (error) {
          fail(error, "Não foi possível remover o tema.");
        }
      },
    });
  };

  return {
    navigate,
    managedUser,
    associated,
    available,
    loading,
    saving,
    dialog,
    addTemas,
    removeTema,
  };
};
